import { PDFDocument, degrees } from 'pdf-lib';
import { isHeicFile, toDisplayableBlob } from '../../image/shared/heicDisplay';

async function loadPdf(file) {
    const bytes = await file.arrayBuffer();
    try {
        return await PDFDocument.load(bytes, { ignoreEncryption: true });
    } catch (err) {
        if (String(err?.message || '').toLowerCase().includes('encrypt')) {
            throw new Error('password');
        }
        throw err;
    }
}

function opacityValue(percent) {
    const n = Number(percent);
    if (!Number.isFinite(n)) return 0.35;
    return Math.min(1, Math.max(0, n / 100));
}

function clampPercent(value, fallback = 50) {
    const n = Number(value);
    if (!Number.isFinite(n)) return fallback;
    return Math.min(100, Math.max(0, n));
}

/** Unrotated box origin from percents (PDF bottom-left space). */
function placementCoords(pageW, pageH, itemW, itemH, xPercent, yPercent) {
    const xp = clampPercent(xPercent, 50);
    const yp = clampPercent(yPercent, 50);
    return {
        x: (xp / 100) * Math.max(0, pageW - itemW),
        y: (yp / 100) * Math.max(0, pageH - itemH),
    };
}

/**
 * pdf-lib rotates around the image's lower-left corner; the interactive preview
 * rotates around the box center (CSS transform-origin: center). Compute the
 * draw origin so the visual center stays fixed after rotation.
 *
 * CSS positive degrees are clockwise; PDF positive degrees are counter-clockwise,
 * so the UI angle is negated when passed to pdf-lib.
 */
function drawImageCentered(page, image, {
    pageW,
    pageH,
    drawW,
    drawH,
    xPercent,
    yPercent,
    rotationDegrees,
    opacity,
}) {
    const { x: left, y: bottom } = placementCoords(pageW, pageH, drawW, drawH, xPercent, yPercent);
    const cx = left + drawW / 2;
    const cy = bottom + drawH / 2;
    const uiAngle = Number(rotationDegrees) || 0;
    const pdfAngle = -uiAngle;
    const rad = (pdfAngle * Math.PI) / 180;
    const cos = Math.cos(rad);
    const sin = Math.sin(rad);

    page.drawImage(image, {
        x: cx - (drawW / 2) * cos + (drawH / 2) * sin,
        y: cy - (drawW / 2) * sin - (drawH / 2) * cos,
        width: drawW,
        height: drawH,
        opacity,
        rotate: degrees(pdfAngle),
    });
}

async function renderTextWatermarkImage(text, fontSize, textColor = '#000000') {
    const safeText = String(text || '').trim();
    const size = Number(fontSize) || 48;
    const fontSpec = `700 ${size}px "Kalpurush", "SolaimanLipi", "Noto Sans Bengali", sans-serif`;

    if (document?.fonts?.load) {
        try {
            await document.fonts.load(fontSpec, safeText);
        } catch {
            // Best effort only; canvas will still render with fallbacks if needed.
        }
    }

    const measureCanvas = document.createElement('canvas');
    const measureCtx = measureCanvas.getContext('2d');
    measureCtx.font = fontSpec;
    const metrics = measureCtx.measureText(safeText);
    const paddingX = Math.ceil(size * 0.5);
    const paddingY = Math.ceil(size * 0.4);
    const width = Math.max(8, Math.ceil(metrics.width + paddingX * 2));
    const height = Math.max(8, Math.ceil(size * 1.6 + paddingY * 2));

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext('2d');
    ctx.clearRect(0, 0, width, height);
    ctx.font = fontSpec;
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillStyle = textColor || '#000000';
    ctx.fillText(safeText, paddingX, height / 2);

    const blob = await new Promise((resolve, reject) => {
        canvas.toBlob((value) => {
            if (value) resolve(value);
            else reject(new Error('textRenderFailed'));
        }, 'image/png');
    });
    return {
        blob,
        width,
        height,
    };
}

async function embedImage(pdf, file) {
    const name = file.name.toLowerCase();
    const type = (file.type || '').toLowerCase();
    if (type === 'image/png' || (!type && name.endsWith('.png'))) {
        return pdf.embedPng(await file.arrayBuffer());
    }
    if (type === 'image/jpeg' || type === 'image/jpg' || (!type && /\.jpe?g$/.test(name))) {
        return pdf.embedJpg(await file.arrayBuffer());
    }
    // pdf-lib embeds only PNG and JPEG. WebP / AVIF are drawn by the browser and
    // HEIC decoded first, then kept as PNG so a see-through logo stays see-through.
    return pdf.embedPng(await (await rasterToPngBlob(file)).arrayBuffer());
}

async function rasterToPngBlob(file) {
    const drawable = await toDisplayableBlob(file);
    if (isHeicFile(file) && drawable === file) throw new Error('invalidImage');
    const url = URL.createObjectURL(drawable);
    try {
        const img = await new Promise((resolve, reject) => {
            const el = new Image();
            el.onload = () => resolve(el);
            el.onerror = () => reject(new Error('invalidImage'));
            el.src = url;
        });
        const canvas = document.createElement('canvas');
        canvas.width = img.naturalWidth;
        canvas.height = img.naturalHeight;
        canvas.getContext('2d').drawImage(img, 0, 0);
        return await new Promise((resolve, reject) => {
            canvas.toBlob((blob) => (blob ? resolve(blob) : reject(new Error('invalidImage'))), 'image/png');
        });
    } finally {
        URL.revokeObjectURL(url);
    }
}

/**
 * @param {File} file
 * @param {{ text: string, textColor?: string, opacity: number, fontSize: number, xPercent: number, yPercent: number, rotationDegrees?: number }} options
 */
export async function applyTextWatermark(file, options) {
    const text = String(options.text || '').trim();
    if (!text) throw new Error('emptyText');

    const pdf = await loadPdf(file);
    const opacity = opacityValue(options.opacity);
    const size = options.fontSize || 48;
    const xPercent = clampPercent(options.xPercent, 50);
    const yPercent = clampPercent(options.yPercent, 50);
    const rotationDegrees = Number(options.rotationDegrees) || 0;
    const textImage = await renderTextWatermarkImage(text, size, options.textColor);
    const pngBytes = await textImage.blob.arrayBuffer();
    const embedded = await pdf.embedPng(pngBytes);

    for (const page of pdf.getPages()) {
        const { width, height } = page.getSize();
        const drawW = Math.min(width * 0.85, textImage.width);
        const drawH = (textImage.height / textImage.width) * drawW;
        drawImageCentered(page, embedded, {
            pageW: width,
            pageH: height,
            drawW,
            drawH,
            xPercent,
            yPercent,
            rotationDegrees,
            opacity,
        });
    }

    const output = await pdf.save();
    return new Blob([output], { type: 'application/pdf' });
}

/**
 * @param {File} file
 * @param {File} imageFile
 * @param {{ opacity: number, xPercent: number, yPercent: number, rotationDegrees?: number, scale: number }} options scale = % of page width
 */
export async function applyImageWatermark(file, imageFile, options) {
    if (!imageFile) throw new Error('noImage');

    const pdf = await loadPdf(file);
    const image = await embedImage(pdf, imageFile);
    const opacity = opacityValue(options.opacity);
    const scalePct = Math.min(100, Math.max(5, Number(options.scale) || 25)) / 100;
    const xPercent = clampPercent(options.xPercent, 50);
    const yPercent = clampPercent(options.yPercent, 50);
    const rotationDegrees = Number(options.rotationDegrees) || 0;

    const dims = image.scale(1);
    for (const page of pdf.getPages()) {
        const { width, height } = page.getSize();
        const drawW = width * scalePct;
        const drawH = (dims.height / dims.width) * drawW;
        drawImageCentered(page, image, {
            pageW: width,
            pageH: height,
            drawW,
            drawH,
            xPercent,
            yPercent,
            rotationDegrees,
            opacity,
        });
    }

    const output = await pdf.save();
    return new Blob([output], { type: 'application/pdf' });
}
